import React, { useState, useEffect, useMemo } from 'react';
import MaterialTable from 'material-table';
import { IconButton, Tooltip, withStyles, Typography } from '@material-ui/core';
import { BackButton, localization, getDimensionsOfPagination } from '../../../sc-utilities';
import { Refresh } from '@material-ui/icons';
import * as actions from './store/erasureReportActions';
import * as action from '../../../np-core/Auth/store/authActions';
import { connect } from 'react-redux';
import Toolbar from '../../../sc-utilities/Toolbar'
import { CancelToken } from 'axios';
import { MuiTableRows } from '../../../../themes';
import { createOrderQuery } from '../../utils';
import moment from 'moment';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        margin: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const SinglePDFReportList = props => {
    const { classes, translations, saveColumnsFilter, tableColumns, fetchErasureReportCount, fetchErasureReport, fetchDiagnoseReportCount, fetchDiagnoseReport, activeCustomerID, regenerateAction, tableRef, loading, erasureReportType = 'pdf' } = props;
    let columnObject = useMemo(() => {
        return tableColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading.productTypes]);
    const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    let reportType = 'ErasureReport';
    let title = translations["single_pdf_reports"] || 'Single pdf reports';
    let fetchReportCountFn = fetchErasureReportCount;
    let fetchReportsFn = fetchErasureReport;
    
    switch (erasureReportType) {
        case 'pdf':
            title = translations["single_pdf_reports"] || 'Single pdf reports';
            reportType = 'ErasureReport';
            fetchReportCountFn = fetchErasureReportCount;
            fetchReportsFn = fetchErasureReport;
            break;
        case 'diagnose-pdf':
            title = translations["diagnose_reports"] || "Diagnose reports";
            reportType = 'DiagnoseReport';
            fetchReportCountFn = fetchDiagnoseReportCount;
            fetchReportsFn = fetchDiagnoseReport;
            break;
        case 'verification-pdf':
            title = translations["verification_reports"] || "Verification reports"
            reportType = 'VerificationReport';
            fetchReportCountFn = fetchErasureReportCount;
            fetchReportsFn = fetchErasureReport;
            break;
        default:
    }

    const pageHeight = erasureReportType === 'diagnose-pdf' ? 200 : 300;

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: false,
        exportFileName: reportType,
        filterCellStyle: { maxWidth: '200px' },
        search: false,
        minBodyHeight: window.innerHeight - pageHeight < 300 ? 300 : window.innerHeight - pageHeight,
        maxBodyHeight: window.innerHeight - pageHeight,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    useEffect(() => {
        if (regenerateAction > 0) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [regenerateAction, tableRef])

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        let erasureReportTypeQuery = [];
        if (erasureReportType === 'pdf') {
            erasureReportTypeQuery = ['pdf', 'check'];
        } else {
            erasureReportTypeQuery = [erasureReportType];
        }
        q['ErasureReportType'] = erasureReportTypeQuery;
        query['q'] = q;
        q['RequestedAt'] = q['RequestedAt'] ? moment(q['RequestedAt']).format('YYYY-MM-DD') : null;
        query['orders'] = query?.orderBy?.field ? createOrderQuery(query?.orderBy?.field, tableRef?.current?.dataManager?.orderDirection ?? 'asc') : [];

        let count = await fetchReportCountFn(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await fetchReportsFn(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[reportType] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{title}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    return (
        <>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                localization={localization(translations)}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </>)
}

const mapDispatchToProps = dispatch => {
    return {
        fetchErasureReportCount: (filter, options) => dispatch(actions.fetchErasureReportCount(filter, options)),
        fetchErasureReport: (filter, options) => dispatch(actions.fetchErasureReport(filter, options)),
        fetchDiagnoseReportCount: (filter, options) => dispatch(actions.fetchDiagnoseReportCount(filter, options)),
        fetchDiagnoseReport: (filter, options) => dispatch(actions.fetchDiagnoseReport(filter, options)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data))
    };
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        erasureReport: state.reports.erasureReports,
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(SinglePDFReportList));
